import { ApolloError } from '@apollo/client';

import { MerchantOrderTypeEnum } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

/**
 * The untracked item "Other" (ContainerTypes.Id -34). It stands in for anything
 * that has no product of its own, so an order carrying it has to say in the ops
 * comment what is actually meant. Bound to the id, not the name, like colugo's
 * UNTRACKED_OTHER_ITEM_TYPE_ID in opsOrderLineRules.ts — keep the two in step.
 */
export const UNTRACKED_OTHER_ITEM_TYPE_ID = -34;

export function isUntrackedOtherLine(line: {
  kind?: string;
  type_id: number;
}): boolean {
  return (
    line.kind !== 'store_asset' && line.type_id === UNTRACKED_OTHER_ITEM_TYPE_ID
  );
}

/**
 * Only outbound orders ship something that has to be picked, so only they need
 * a line. Mirrors colugo's OUTBOUND_MERCHANT_ORDER_TYPES: inbound orders
 * (pickups, returns, terminations, inventories) describe what comes back and
 * legitimately carry no line.
 */
const ORDER_TYPES_REQUIRING_LINES = new Set<MerchantOrderTypeEnum>([
  MerchantOrderTypeEnum.Resupply,
  MerchantOrderTypeEnum.Onboarding,
  MerchantOrderTypeEnum.OnboardingS,
  MerchantOrderTypeEnum.OnboardingM,
  MerchantOrderTypeEnum.OnboardingL,
  MerchantOrderTypeEnum.Special,
  MerchantOrderTypeEnum.Sample,
  MerchantOrderTypeEnum.EventsSample,
  MerchantOrderTypeEnum.WarehouseTransfer,
  MerchantOrderTypeEnum.EventAccountOrder,
  MerchantOrderTypeEnum.EventBufferOrder,
]);

export function orderTypeRequiresLines(
  orderType: MerchantOrderTypeEnum | null | undefined,
): boolean {
  return orderType != null && ORDER_TYPES_REQUIRING_LINES.has(orderType);
}

export function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}

const SAVE_ERROR_DESCRIPTIONS: Record<string, string> = {
  OPS_ORDER_NO_ITEMS: 'Order:validationNoItems',
  OPS_ORDER_OTHER_ITEM_REQUIRES_OPS_COMMENT:
    'Order:validationOpsCommentRequiredForOther',
};

/**
 * The sentence to show under a failed-save toast: colugo's line rules map to
 * the same wording the form itself validates with, anything else falls back
 * to the reason colugo attached (or nothing, leaving the generic toast).
 */
export function describeOpsOrderSaveError(err: unknown): string | undefined {
  if (!(err instanceof ApolloError)) return undefined;
  for (const gqlErr of err.graphQLErrors ?? []) {
    const ext = gqlErr.extensions as
      | { exception?: { error?: unknown; reason?: unknown } }
      | undefined;
    const code =
      typeof ext?.exception?.error === 'string'
        ? ext.exception.error
        : gqlErr.message;
    const translationKey = SAVE_ERROR_DESCRIPTIONS[code];
    if (translationKey) return i18n.t(translationKey);
    const reason = ext?.exception?.reason;
    if (typeof reason === 'string' && reason.trim()) return reason;
  }
  return undefined;
}
